import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { api, ApiError, hasLocalAdminKey, setLocalAdminKey } from "./api";
import type { AdminActor } from "./types";
import { AppLayout } from "./components/AppLayout";
import { DashboardPage } from "./pages/DashboardPage";
import { ClientsPage } from "./pages/ClientsPage";
import { ClientDetailPage } from "./pages/ClientDetailPage";
import { ScansPage } from "./pages/ScansPage";
import { ScanDetailPage } from "./pages/ScanDetailPage";
import { SettingsPage } from "./pages/SettingsPage";
import { CleanupPage } from "./pages/CleanupPage";
import { AuditPage } from "./pages/AuditPage";
import { AlertsPage } from "./pages/AlertsPage";
import { AdministratorsPage } from "./pages/AdministratorsPage";
import { AzurePoolPage } from "./pages/AzurePoolPage";

export default function App() {
  const [admin, setAdmin] = useState<AdminActor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api<{ admin: AdminActor }>("/admin/session");
      setAdmin(result.admin);
    } catch (e) {
      setAdmin(null);
      if (e instanceof ApiError && e.status !== 401) setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  if (loading)
    return (
      <Box minHeight="100vh" display="grid" sx={{ placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  if (!admin)
    return (
      <LoginPage
        error={error}
        onLogin={async (key) => {
          setLocalAdminKey(key);
          await load();
        }}
      />
    );
  return (
    <Routes>
      <Route element={<AppLayout admin={admin} />}>
        <Route index element={<DashboardPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/:clientId" element={<ClientDetailPage />} />
        <Route path="scans" element={<ScansPage />} />
        <Route path="scans/:scanId" element={<ScanDetailPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="azure-pool" element={<AzurePoolPage />} />
        <Route path="cleanup" element={<CleanupPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="administrators" element={<AdministratorsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function LoginPage({
  error,
  onLogin,
}: {
  error: string;
  onLogin: (key: string) => Promise<void>;
}) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(error);
  const local =
    location.hostname === "localhost" || location.hostname === "127.0.0.1";
  return (
    <Box
      minHeight="100vh"
      display="grid"
      sx={{
        placeItems: "center",
        background:
          "radial-gradient(circle at 20% 20%,#d5f3ef 0,transparent 34%),#f2f6f7",
        p: 2,
      }}
    >
      <Paper sx={{ width: "min(440px,100%)", p: 4 }}>
        <Stack gap={2.5}>
          <div>
            <Typography variant="overline" color="primary" fontWeight={800}>
              OSPREY OCR
            </Typography>
            <Typography variant="h4">Control center</Typography>
            <Typography color="text.secondary" mt={1}>
              {local
                ? "Enter the local ADMIN_API_KEY to continue."
                : "This console is protected by Cloudflare Access. Sign in through the Access prompt, then reload this page."}
            </Typography>
          </div>
          {local && (
            <TextField
              type="password"
              label="Local admin key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              autoFocus
            />
          )}
          {(localError || (!local && !hasLocalAdminKey())) && localError && (
            <Alert severity="error">{localError}</Alert>
          )}
          <Button
            variant="contained"
            size="large"
            disabled={busy || (local && !key)}
            onClick={async () => {
              setBusy(true);
              setLocalError("");
              try {
                await onLogin(key);
              } catch (e) {
                setLocalError(e instanceof Error ? e.message : "Login failed");
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Checking…" : local ? "Continue" : "Reload session"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
